import {
  Transforms,
  Editor,
  Range,
  Element as SlateElement,
  Text,
  Path,
  type BasePoint
} from 'slate';
import {HistoryEditor} from 'slate-history';
import {ReactEditor} from 'slate-react';

import showMessage from '../../../utils/showMessage';
import {
  EMPTY_DISPLAY,
  formatFrameworkFieldValue
} from '../utils/formatFrameworkFieldValue';
import {
  formatCurrency,
  formatPercentage,
  formatDate,
  formatBoolean,
  formatDateRange
} from '../utils/formattingUtils';
import {compressImage} from '../utils/imageUtils';
import {
  appendHtmlAnswerNodes,
  isHtmlAnswerContent
} from '../utils/prepareFrameworkHtmlForInsert';
import {
  deselectIfInvalid,
  isRangeValid,
  scheduleSelectAtDocumentEnd
} from '../utils/selectionHelpers';

export const handlePrint = (): void => {
  const pages = Array.from(document.querySelectorAll('.paginated-page'));

  if (pages.length === 0) {
    window.print();

    return;
  }

  const styleTags = Array.from(document.querySelectorAll('style'))
    .map((el) => el.outerHTML)
    .join('\n');
  const linkTags = Array.from(
    document.querySelectorAll('link[rel="stylesheet"]')
  )
    .map((el) => el.outerHTML)
    .join('\n');
  const pagesMarkup = pages
    .map((page) => {
      const cloned = page.cloneNode(true) as HTMLElement;
      cloned.style.margin = '0 auto';
      cloned.style.boxShadow = 'none';

      return cloned.outerHTML;
    })
    .join('\n');

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    window.print();

    return;
  }

  doc.open();
  doc.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Print Report</title>
        ${linkTags}
        ${styleTags}
        <style>
          @page {
            size: auto;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #fff;
          }
          .print-root {
            width: 100%;
            margin: 0;
            padding: 0;
          }
          .paginated-page {
            break-after: page;
            page-break-after: always;
            box-shadow: none !important;
            margin: 0 auto !important;
          }
          .paginated-page:last-child {
            break-after: auto;
            page-break-after: auto;
          }
          .element-actions-container,
          .element-action-btn,
          .anchor-options-container,
          .table-actions-container,
          .table-menu-trigger,
          .table-drag-handle,
          .resize-handle-table,
          .comment-group-overlay,
          .comment-thread-sidebar,
          .floating-comment-button,
          .selection-popup-menu,
          .page-break-indicator,
          .page-number {
            display: none !important;
          }
          [data-header-footer="true"] {
            display: none !important;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-root">${pagesMarkup}</div>
      </body>
    </html>
  `);
  doc.close();

  const cleanup = (): void => {
    window.setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 100);
  };

  window.setTimeout(() => {
    const printTarget = iframe.contentWindow;
    if (!printTarget) {
      cleanup();
      window.print();

      return;
    }
    printTarget.focus();
    printTarget.onafterprint = cleanup;
    printTarget.print();
  }, 100);
};

/**
 * EditorActions — Pure editor mutation functions.
 */

// ─── Text Formatting ─────────────────────────────────────────────────────────

interface MarkOptions {
  format: string;
  isActive: boolean;
  ranges: Range[];
  isHeaderFooterEditMode: boolean;
  isPathInsideHeaderFooter: (path: number[]) => boolean;
}

export function toggleMark(editor: Editor, options: MarkOptions): void {
  const {
    format,
    isActive,
    ranges,
    isHeaderFooterEditMode,
    isPathInsideHeaderFooter
  } = options;

  Editor.withoutNormalizing(editor, () => {
    ranges.forEach((r) => {
      const isCollapsed = Range.isCollapsed(r);
      const isSelection = editor.selection && Range.equals(r, editor.selection);

      if (isCollapsed && isSelection) {
        // For collapsed selection (cursor), use Editor methods
        if (isActive) {
          Editor.removeMark(editor, format);
        } else {
          if (format === 'superscript') {
            Editor.removeMark(editor, 'subscript');
          } else if (format === 'subscript') {
            Editor.removeMark(editor, 'superscript');
          }
          Editor.addMark(editor, format, true);
        }
      } else {
        const isHF = isPathInsideHeaderFooter(r.anchor.path as number[]);
        if (isHF && !isHeaderFooterEditMode) return;

        if (isActive) {
          Transforms.unsetNodes(editor, format, {
            at: r,
            match: (n) => Text.isText(n),
            split: true
          });
        } else {
          const props: Record<string, any> = {[format]: true};
          if (format === 'superscript') {
            props.subscript = null;
          } else if (format === 'subscript') {
            props.superscript = null;
          }

          Transforms.setNodes(editor, props, {
            at: r,
            match: (n) => Text.isText(n),
            split: true
          });
        }
      }
    });
  });
  ReactEditor.focus(editor as ReactEditor);
}

interface BlockOptions {
  format: string;
  isActive: boolean;
  ranges: Range[];
  isHeaderFooterEditMode: boolean;
  isPathInsideHeaderFooter: (path: number[]) => boolean;
}

export function toggleBlock(editor: Editor, options: BlockOptions): void {
  const {
    format,
    isActive,
    ranges,
    isHeaderFooterEditMode,
    isPathInsideHeaderFooter
  } = options;
  Editor.withoutNormalizing(editor, () => {
    ranges.forEach((r) => {
      if (
        isPathInsideHeaderFooter(r.anchor.path as number[]) &&
        !isHeaderFooterEditMode
      )
        return;
      Transforms.setNodes(
        editor,
        {type: isActive ? 'paragraph' : format},
        {
          at: r,
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            !['link', 'page', 'header-wrapper', 'footer-wrapper'].includes(
              n.type
            )
        }
      );
    });
  });
}

interface AlignmentOptions {
  align: string;
  ranges: Range[];
  isHeaderFooterEditMode: boolean;
  isPathInsideHeaderFooter: (path: number[]) => boolean;
}

export function setAlignment(editor: Editor, options: AlignmentOptions): void {
  const {align, ranges, isHeaderFooterEditMode, isPathInsideHeaderFooter} =
    options;
  Editor.withoutNormalizing(editor, () => {
    ranges.forEach((r) => {
      if (
        isPathInsideHeaderFooter(r.anchor.path as number[]) &&
        !isHeaderFooterEditMode
      )
        return;
      Transforms.setNodes(
        editor,
        {align},
        {
          at: r,
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            [
              'paragraph',
              'heading-one',
              'heading-two',
              'table-cell',
              'list-item',
              'image',
              'placeholder-image',
              'shape'
            ].includes(n.type)
        }
      );
    });
  });
  ReactEditor.focus(editor as ReactEditor);
}

interface TextPropertyOptions {
  property: string;
  value: any;
  ranges: Range[];
  isHeaderFooterEditMode: boolean;
  isPathInsideHeaderFooter: (path: number[]) => boolean;
}

export function setTextProperty(
  editor: Editor,
  options: TextPropertyOptions
): void {
  const {
    property,
    value,
    ranges,
    isHeaderFooterEditMode,
    isPathInsideHeaderFooter
  } = options;
  Editor.withoutNormalizing(editor, () => {
    ranges.forEach((r) => {
      if (
        isPathInsideHeaderFooter(r.anchor.path as number[]) &&
        !isHeaderFooterEditMode
      )
        return;
      Transforms.setNodes(
        editor,
        {[property]: value},
        {at: r, match: (n) => Text.isText(n), split: true}
      );
    });
  });
  ReactEditor.focus(editor as ReactEditor);
}

// ─── Content Insertion ───────────────────────────────────────────────────────

export function applyLinkUrl(
  editor: Editor,
  url: string,
  displayText?: string
): void {
  const href = url.trim();
  if (!href) return;

  const text = displayText?.trim() ? displayText.trim() : href;
  const {selection} = editor;
  if (!selection) return;

  if (Range.isCollapsed(selection)) {
    Transforms.insertNodes(editor, {
      type: 'link',
      url: href,
      children: [{text}]
    });
  } else {
    const selectedText = Editor.string(editor, selection);
    Transforms.wrapNodes(
      editor,
      {type: 'link', url: href, children: []},
      {split: true}
    );
    if (
      displayText !== undefined &&
      displayText.trim() !== '' &&
      text !== selectedText
    ) {
      const linkEntry = Editor.above(editor, {
        match: (n) =>
          SlateElement.isElement(n) && (n as {type?: string}).type === 'link'
      });
      if (linkEntry) {
        const [, linkPath] = linkEntry;
        const innerStart = Editor.start(editor, linkPath);
        const innerEnd = Editor.end(editor, linkPath);
        Transforms.select(editor, {anchor: innerStart, focus: innerEnd});
        Transforms.insertText(editor, text);
      }
    }
    Transforms.collapse(editor, {edge: 'end'});
  }
}

export function insertLink(
  editor: Editor,
  url: string,
  displayText?: string
): void {
  applyLinkUrl(editor, url, displayText);
}

export async function insertImage(editor: Editor, file: File): Promise<void> {
  try {
    const url = await compressImage(file, 800, 0.7);
    Transforms.insertNodes(editor, [
      {type: 'image', url, children: [{text: ''}]} as any,
      {type: 'paragraph', children: [{text: ''}]} as any
    ]);
  } catch (err) {
    // Silently fail, matching original behavior
  }
}

export function insertShape(
  editor: Editor,
  shapeType: string,
  pageOrientation: 'portrait' | 'landscape',
  effectivePageMargins: {left: number; right: number}
): void {
  const isLandscape = pageOrientation === 'landscape';
  const widthInches = isLandscape ? 11.69 : 8.27;
  const w =
    (widthInches - effectivePageMargins.left - effectivePageMargins.right) * 96;
  Transforms.insertNodes(editor, [
    {
      type: 'placeholder-image',
      placeholderType: shapeType,
      width: shapeType === 'banner' || shapeType === 'hero' ? w : 200,
      height: shapeType === 'banner' ? 200 : 150,
      children: [{text: ''}]
    } as any,
    {type: 'paragraph', children: [{text: ''}]} as any
  ]);
}

export function insertTable(editor: Editor): void {
  Transforms.insertNodes(editor, [
    {
      type: 'table',
      children: [
        {
          type: 'table-row',
          children: [
            {type: 'table-cell', children: [{text: 'Row 1, Col 1'}]},
            {type: 'table-cell', children: [{text: 'Row 1, Col 2'}]},
            {type: 'table-cell', children: [{text: 'Row 1, Col 3'}]}
          ]
        },
        {
          type: 'table-row',
          children: [
            {type: 'table-cell', children: [{text: ''}]},
            {type: 'table-cell', children: [{text: ''}]},
            {type: 'table-cell', children: [{text: ''}]}
          ]
        }
      ]
    },
    {type: 'paragraph', children: [{text: ''}]}
  ]);
}

export function removeColumns(editor: Editor): void {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === 'columns-container'
  });
}

export function insertHeader(editor: Editor, type: string = 'default'): void {
  let children: any[] = [];

  if (type === 'three-columns') {
    children = [
      {
        type: 'paragraph',
        align: 'left',
        children: [{text: 'Left Text', fontSize: 10}]
      },
      {
        type: 'paragraph',
        align: 'center',
        children: [{text: 'Center Text', fontSize: 10, bold: true}]
      },
      {
        type: 'paragraph',
        align: 'right',
        children: [{text: 'Right Text', fontSize: 10}]
      }
    ];
  } else if (type === 'banded') {
    children = [
      {
        type: 'paragraph',
        align: 'center',
        children: [{text: 'Banded Header', fontSize: 14, bold: true}]
      }
    ];
  } else {
    children = [
      {
        type: 'paragraph',
        align: 'center',
        children: [{text: 'Document Header', bold: true, fontSize: 14}]
      }
    ];
  }

  const header = {
    type: 'header-wrapper',
    headerType: type,
    children
  };

  Editor.withoutNormalizing(editor, () => {
    // Replace any existing header template with the newly selected one.
    Transforms.removeNodes(editor, {
      at: [],
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === 'header-wrapper'
    });

    const firstNode = editor.children[0] as any;
    const insertPath =
      firstNode?.type === 'page' ? ([0, 0] as Path) : ([0] as Path);
    Transforms.insertNodes(editor, header as any, {at: insertPath});
  });
}

export function insertFooter(editor: Editor, type: string = 'default'): void {
  let children: any[] = [];

  if (type === 'three-columns') {
    children = [
      {
        type: 'paragraph',
        align: 'left',
        children: [{text: 'Footer Left', fontSize: 10}]
      },
      {
        type: 'paragraph',
        align: 'center',
        children: [{text: 'Footer Center', fontSize: 10}]
      },
      {
        type: 'paragraph',
        align: 'right',
        children: [{text: 'Footer Right', fontSize: 10}]
      }
    ];
  } else if (type === 'banded') {
    children = [
      {
        type: 'paragraph',
        align: 'center',
        children: [{text: 'Banded Footer', fontSize: 10, bold: true}]
      }
    ];
  } else {
    children = [
      {
        type: 'paragraph',
        align: 'center',
        children: [{text: 'Document Footer', fontSize: 10}]
      }
    ];
  }

  const footer = {
    type: 'footer-wrapper',
    footerType: type,
    children
  };

  Editor.withoutNormalizing(editor, () => {
    // Replace any existing footer template with the newly selected one.
    Transforms.removeNodes(editor, {
      at: [],
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === 'footer-wrapper'
    });

    const firstNode = editor.children[0] as any;
    const insertPath =
      firstNode?.type === 'page'
        ? ([0, firstNode.children?.length ?? 0] as Path)
        : ([editor.children.length] as Path);
    Transforms.insertNodes(editor, footer as any, {
      at: insertPath
    });
  });
}

export function deleteHeader(editor: Editor): void {
  Editor.withoutNormalizing(editor, () => {
    if (editor.selection) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          at: editor.selection,
          match: (n) => SlateElement.isElement(n) && n.type === 'header-wrapper'
        })
      );
      if (match) {
        Transforms.deselect(editor);
        editor.selection = null;
      }
    }

    const matches = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => SlateElement.isElement(n) && n.type === 'header-wrapper'
      })
    );

    const paths = matches
      .map(([, p]) => p)
      .sort(Path.compare)
      .reverse();
    for (const path of paths) {
      if (Editor.hasPath(editor, path)) {
        Transforms.removeNodes(editor, {at: path});
      }
    }
  });
}

export function deleteFooter(editor: Editor): void {
  Editor.withoutNormalizing(editor, () => {
    if (editor.selection) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          at: editor.selection,
          match: (n) => SlateElement.isElement(n) && n.type === 'footer-wrapper'
        })
      );
      if (match) {
        Transforms.deselect(editor);
        editor.selection = null;
      }
    }

    const matches = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => SlateElement.isElement(n) && n.type === 'footer-wrapper'
      })
    );

    const paths = matches
      .map(([, p]) => p)
      .sort(Path.compare)
      .reverse();
    for (const path of paths) {
      if (Editor.hasPath(editor, path)) {
        Transforms.removeNodes(editor, {at: path});
      }
    }
  });
}

export function insertPageBreak(editor: Editor): void {
  Transforms.insertNodes(editor, [
    {type: 'page-break', children: [{text: ''}]},
    {type: 'paragraph', children: [{text: ''}]}
  ]);
}

export function insertPageNumber(editor: Editor, settings?: any): void {
  const {
    position = 'footer',
    alignment = 'right',
    format = '123'
  } = settings || {};

  const targetType =
    position === 'header' ? 'header-wrapper' : 'footer-wrapper';
  const targetKey = position === 'header' ? 'headerType' : 'footerType';

  Editor.withoutNormalizing(editor, () => {
    const matches = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === targetType
      })
    );
    const paths = matches
      .map(([, p]) => p)
      .sort(Path.compare)
      .reverse();
    for (const path of paths) {
      if (Editor.hasPath(editor, path)) {
        Transforms.removeNodes(editor, {at: path});
      }
    }
    const wrapperNode = {
      type: targetType,
      [targetKey]: 'default',
      children: [
        {
          type: 'paragraph',
          align: alignment,
          children: [
            {
              type: 'page-number',
              format,
              children: [{text: ''}]
            }
          ]
        }
      ]
    };
    const firstNode = editor.children[0] as any;
    let insertPath: Path;
    if (position === 'header') {
      insertPath =
        firstNode?.type === 'page' ? ([0, 0] as Path) : ([0] as Path);
    } else {
      insertPath =
        firstNode?.type === 'page'
          ? ([0, firstNode.children?.length ?? 0] as Path)
          : ([editor.children.length] as Path);
    }
    Transforms.insertNodes(editor, wrapperNode as any, {
      at: insertPath
    });
  });

  // const hasTarget = editor.children.some(
  //   (page: any) =>
  //     page.type === 'page' &&
  //     page.children.some((n: any) => n.type === targetType)
  // );

  // if (!hasTarget) {
  //   if (position === 'header') insertHeader(editor, 'default');
  //   else insertFooter(editor, 'default');
  // }

  // Editor.withoutNormalizing(editor, () => {
  //   const match = Array.from(
  //     Editor.nodes(editor, {
  //       match: (n) => SlateElement.isElement(n) && n.type === targetType,
  //       at: []
  //     })
  //   )[0];

  //   if (match) {
  //     const [, targetPath] = match;
  //     // Create the paragraph and page number block
  //     const pageNumNode = {
  //       type: 'paragraph',
  //       align: alignment,
  //       children: [
  //         {
  //           type: 'page-number',
  //           format,
  //           children: [{text: ''}]
  //         }
  //       ]
  //     };

  //     // Append it to the wrapper
  //     Transforms.insertNodes(editor, pageNumNode as any, {
  //       at: [...targetPath, 0] // Insert at the top of the header/footer
  //     });
  //   }
  // });
}

export function removePageNumbers(editor: Editor): void {
  Editor.withoutNormalizing(editor, () => {
    if (editor.selection) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          at: editor.selection,
          match: (n) =>
            SlateElement.isElement(n) &&
            (n.type === 'page-number' ||
              n.type === 'header-wrapper' ||
              n.type === 'footer-wrapper')
        })
      );
      if (match) {
        Transforms.deselect(editor);
        editor.selection = null;
      }
    }

    const matches = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) =>
          SlateElement.isElement(n) &&
          (n.type === 'page-number' ||
            n.type === 'header-wrapper' ||
            n.type === 'footer-wrapper')
      })
    );

    const paths = matches
      .map(([, p]) => p)
      .sort(Path.compare)
      .reverse();
    for (const path of paths) {
      if (Editor.hasPath(editor, path)) {
        Transforms.removeNodes(editor, {at: path});
      }
    }
  });
}

export function handleCropComplete(
  editor: Editor,
  croppedImageBase64: string
): void {
  Transforms.insertNodes(editor, [
    {
      type: 'image',
      url: croppedImageBase64,
      children: [{text: ''}]
    } as any,
    {type: 'paragraph', children: [{text: ''}]} as any
  ]);
}

// ─── Comments ────────────────────────────────────────────────────────────────

interface CommentOptions {
  activeUser: any;
  metadata: any;
  onCommentCreated: (threadId: string) => void;
}

export function insertComment(editor: Editor, options: CommentOptions): void {
  const {activeUser, metadata, onCommentCreated} = options;
  const {selection} = editor;
  if (!selection) {
    showMessage({
      status: 'warning',
      message: 'Please place the cursor in the paragraph to comment on.'
    });

    return;
  }
  const marks = Editor.marks(editor);
  const existingThreadId =
    marks && typeof marks.commentThreadId === 'string'
      ? marks.commentThreadId
      : null;
  const threadId = existingThreadId ?? `thread_${Date.now()}`;
  if (!existingThreadId) {
    if (Range.isCollapsed(selection)) {
      showMessage({
        status: 'warning',
        message: 'Please select text to comment on.'
      });

      return;
    }
    Editor.addMark(editor, 'commentThreadId', threadId);
  }
  type CommentableBlock = SlateElement & {comments?: unknown[]};
  const blockEntry = Editor.above<CommentableBlock>(editor, {
    match: (n): n is CommentableBlock =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      Editor.isBlock(editor, n)
  });
  if (!blockEntry) return;
  const [block, path] = blockEntry;
  const newComment = {
    id: `c${Date.now()}`,
    threadId,
    comment: '',
    isNew: true,
    user_id: activeUser?.id,
    userFirstName:
      activeUser?.userFirstName || activeUser?.name?.split(' ')[0] || 'Unknown',
    userLastName:
      activeUser?.userLastName || activeUser?.name?.split(' ')[1] || '',
    createdAt: new Date().toISOString(),
    updateAt: new Date().toISOString(),
    documentId: metadata?.documentId,
    status: 'open',
    replies: []
  };
  const updatedBlock: Partial<CommentableBlock> = {
    comments: [...(block.comments ?? []), newComment]
  };
  Transforms.setNodes(editor, updatedBlock, {at: path});
  onCommentCreated(threadId);
}

// ─── Document Operations ─────────────────────────────────────────────────────

export function clearDocument(editor: Editor): void {
  Editor.withoutNormalizing(editor, () => {
    Transforms.removeNodes(editor, {
      at: [],
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'page'
    });
    editor.children = [
      {
        type: 'page',
        id: 'page-0',
        pageNumber: 0,
        children: [{type: 'paragraph', children: [{text: ''}]}]
      }
    ];
    editor.onChange();
  });
}

// ─── Sidebar Content Insertion (moved from window event handler) ─────────────

/** Partial/empty structured answers use —; [No Answer] is only when answer is null at insert time. */
const normalizeDisplayText = (text: string): string => {
  const t = text.trim();
  if (!t || t === EMPTY_DISPLAY) return EMPTY_DISPLAY;

  return text;
};

const looksLikeJsonString = (s: string): boolean => {
  const t = s.trim();

  return (
    (t.startsWith('{') && t.endsWith('}')) ||
    (t.startsWith('[') && t.endsWith(']'))
  );
};

/**
 * Converts a sidebar answer value into proper Slate nodes.
 * Handles all answer types: text, number, currency, percentage, date,
 * daterange, boolean, email, link, list, table, html, bulk-qa.
 */
export function getNodes(content: any, prefix = ''): any[] {
  const nodes: any[] = [];

  if (typeof content === 'string') {
    nodes.push({
      type: 'paragraph',
      source: 'sidebar',
      children: prefix
        ? [{text: prefix, bold: true}, {text: content}]
        : [{text: content}]
    });
  } else if (content?.type === 'table') {
    if (prefix)
      nodes.push({
        type: 'paragraph',
        source: 'sidebar',
        children: [{text: prefix, bold: true}]
      });

    const normalizedHeaders = Array.isArray(content.headers)
      ? content.headers
          .map((header: any, index: number) => {
            if (typeof header === 'string') {
              return {key: header, label: header};
            }

            if (header && typeof header === 'object') {
              const key = String(header.key ?? header.label ?? `col_${index}`);
              const label = String(
                header.label ?? header.key ?? `Column ${index + 1}`
              );

              return {key, label};
            }

            return null;
          })
          .filter(Boolean)
      : [];

    const normalizedRows = Array.isArray(content.rows) ? content.rows : [];

    nodes.push({
      type: 'table',
      source: 'sidebar',
      children: [
        {
          type: 'table-row',
          children: normalizedHeaders.map((header: any) => ({
            type: 'table-cell',
            children: [{text: String(header.label ?? ''), bold: true}]
          }))
        },
        ...normalizedRows.map((row: any) => {
          const isRowArray = Array.isArray(row);

          return {
            type: 'table-row',
            children: normalizedHeaders.map((header: any, index: number) => {
              const cellValue = isRowArray
                ? row[index]
                : row && typeof row === 'object'
                  ? row[header.key]
                  : '';
              const display =
                cellValue !== null &&
                cellValue !== undefined &&
                typeof cellValue === 'object'
                  ? formatFrameworkFieldValue(cellValue)
                  : String(cellValue ?? '');

              return {
                type: 'table-cell',
                children: [{text: display}]
              };
            })
          };
        })
      ]
    });
    nodes.push({
      type: 'paragraph',
      source: 'sidebar',
      children: [{text: ''}]
    });
  } else if (content?.type === 'list') {
    if (prefix)
      nodes.push({
        type: 'paragraph',
        source: 'sidebar',
        children: [{text: prefix, bold: true}]
      });

    let items: string[] = [];
    try {
      const parsed =
        typeof content.value === 'string'
          ? JSON.parse(content.value)
          : content.value;
      items = Array.isArray(parsed)
        ? parsed.map((v: any) => formatFrameworkFieldValue(v))
        : [formatFrameworkFieldValue(content.value ?? '')];
    } catch {
      items = [String(content.value ?? '')];
    }

    if (items.length > 0) {
      nodes.push({
        type: 'bulleted-list',
        source: 'sidebar',
        children: items.map((item) => ({
          type: 'list-item',
          source: 'sidebar',
          children: [{text: item}]
        }))
      });
    } else {
      nodes.push({
        type: 'paragraph',
        source: 'sidebar',
        children: [{text: ''}]
      });
    }

    nodes.push({
      type: 'paragraph',
      source: 'sidebar',
      children: [{text: ''}]
    });
  } else if (isHtmlAnswerContent(content)) {
    appendHtmlAnswerNodes(nodes, content.value, prefix);
  } else if (content) {
    const rawVal = content.value;
    let text: string;
    if (rawVal === undefined || rawVal === null) {
      text = '';
    } else if (
      typeof rawVal === 'object' ||
      (typeof rawVal === 'string' && looksLikeJsonString(rawVal))
    ) {
      text = formatFrameworkFieldValue(rawVal);
    } else {
      text = String(rawVal);
    }
    const marks: any = {};
    let isLink = false;
    let linkUrl = '';
    switch (content.type) {
      case 'currency':
        text = formatCurrency(text);
        break;
      case 'percentage':
        text = formatPercentage(text);
        break;
      case 'date':
        text = `📅 ${formatDate(text)}`;
        break;
      case 'daterange':
        text = formatDateRange(text);
        break;
      case 'email':
        linkUrl = `mailto:${text}`;
        isLink = true;
        break;
      case 'link':
        linkUrl = text.startsWith('http') ? text : `https://${text}`;
        isLink = true;
        break;
      case 'number':
        text = String(text);
        break;
      case 'boolean': {
        const b = formatBoolean(text);
        text = b.text;
        if (b.color) marks.color = b.color;
        if (b.bold) marks.bold = b.bold;
        break;
      }
    }
    text = normalizeDisplayText(text);
    const children = isLink
      ? [{type: 'link', url: linkUrl, children: [{text, ...marks}]}]
      : [{text, ...marks}];
    nodes.push({
      type: 'paragraph',
      source: 'sidebar',
      children: prefix ? [{text: prefix, bold: true}, ...children] : children
    });
  }

  return nodes;
}

function normalizeSlateNodesForInsert(nodes: any[]): any[] {
  const structuralNodeTypes = new Set([
    'table',
    'table-row',
    'columns-container',
    'column',
    'page'
  ]);

  const normalizeTextLeaf = (leaf: any): any => {
    if (!leaf || typeof leaf !== 'object') {
      return {text: String(leaf ?? '')};
    }
    if (typeof leaf.text !== 'string') {
      return {...leaf, text: String(leaf.text ?? '')};
    }

    return leaf;
  };

  const normalizeNode = (node: any): any => {
    if (!node || typeof node !== 'object') {
      return {
        type: 'paragraph',
        source: 'sidebar',
        children: [{text: String(node ?? '')}]
      };
    }

    // Text leaf
    if ('text' in node && !('type' in node)) {
      return normalizeTextLeaf(node);
    }

    const rawChildren = Array.isArray(node.children) ? node.children : [];
    const normalizedChildren = rawChildren.map((child: any) => {
      if (
        child &&
        typeof child === 'object' &&
        ('type' in child || 'text' in child)
      ) {
        return 'text' in child && !('type' in child)
          ? normalizeTextLeaf(child)
          : normalizeNode(child);
      }

      return normalizeTextLeaf(child);
    });

    return {
      ...node,
      children:
        normalizedChildren.length > 0
          ? normalizedChildren
          : structuralNodeTypes.has(String(node.type))
            ? []
            : [{text: ''}]
    };
  };

  return (Array.isArray(nodes) ? nodes : [])
    .filter(Boolean)
    .map((node) => normalizeNode(node));
}

const SIDEBLOCK_MATCH = (editor: Editor) => (n: any) =>
  !Editor.isEditor(n) && SlateElement.isElement(n) && Editor.isBlock(editor, n);

function resolveSidebarInsertPoint(editor: Editor): Path | BasePoint | null {
  deselectIfInvalid(editor);

  try {
    if (editor.selection && isRangeValid(editor, editor.selection)) {
      const currentBlock = Editor.above(editor, {
        at: editor.selection,
        match: SIDEBLOCK_MATCH(editor)
      });

      if (currentBlock) {
        return Path.next(currentBlock[1]);
      }
    }

    return Editor.end(editor, []);
  } catch {
    try {
      return Editor.end(editor, []);
    } catch {
      return null;
    }
  }
}

function insertSidebarNodesAtDocumentEnd(
  editor: Editor,
  safeNodes: any[],
  options: {afterReflowMs?: number}
): void {
  if (!safeNodes.length) return;

  const insertPoint = resolveSidebarInsertPoint(editor);

  Transforms.deselect(editor);
  if (insertPoint) {
    Transforms.insertNodes(editor, safeNodes, {
      select: false,
      at: insertPoint
    });
  } else {
    Transforms.insertNodes(editor, safeNodes, {select: false});
  }

  deselectIfInvalid(editor);
  scheduleSelectAtDocumentEnd(editor, {
    afterReflowMs: options.afterReflowMs
  });
}

function focusAndScrollToSelection(editor: Editor): void {
  ReactEditor.focus(editor as ReactEditor);
  if (typeof window === 'undefined') return;

  window.requestAnimationFrame(() => {
    try {
      if (!editor.selection) return;
      const domRange = ReactEditor.toDOMRange(
        editor as ReactEditor,
        editor.selection
      );
      const node = domRange.startContainer;
      const element =
        node.nodeType === window.Node.ELEMENT_NODE
          ? (node as Element)
          : node.parentElement;
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    } catch (e) {
      // Ignore non-DOM selection timing errors.
    }
  });
}

/**
 * Maps a sidebar heading block's logical level to the Slate heading element type
 * used in the document. Level 'one' is intentionally demoted to 'heading-two' at
 * insert time so document Heading 1 stays reserved for the report title — the
 * sidebar still displays the item with its original heading_1 styling.
 */
const sidebarHeadingLevelToSlateType = (
  level: string | undefined
): 'heading-one' | 'heading-two' | 'heading-three' => {
  if (level === 'three') return 'heading-three';
  if (level === 'two') return 'heading-two';
  if (level === 'one') return 'heading-two';

  return 'heading-two';
};

/**
 * Processes content from the Sidebar (previously via window event handler).
 * Called by Editor when it detects a new pendingInsert from the editorStore.
 */
export function processInsertContent(
  editor: Editor,
  content: any,
  pendingBulkInsertRef: React.MutableRefObject<boolean>
): void {
  if (!content) return;

  HistoryEditor.withNewBatch(editor as HistoryEditor, () => {
    if (content.type === 'field-group') {
      const blocks = Array.isArray(content.blocks) ? content.blocks : [];
      if (!blocks.length) return;

      const nodes: any[] = [];
      blocks.forEach((b: any) => {
        if (!b) return;
        if (b.kind === 'heading') {
          const text = String(b.text ?? '').trim();
          if (!text) return;
          const level = b.level ?? 'one';
          const headingType = sidebarHeadingLevelToSlateType(level);
          nodes.push({
            type: headingType,
            source: 'sidebar',
            children: [{text}]
          });
          nodes.push({
            type: 'paragraph',
            source: 'sidebar',
            children: [{text: ''}]
          });

          return;
        }

        if (b.kind === 'qa') {
          const q = String(b.question ?? '').trim();
          // If label is missing, still insert the answer but don't show any fallback label.
          if (q) {
            nodes.push({
              type: 'paragraph',
              source: 'sidebar',
              children: [{text: q, bold: true}]
            });
          }

          if (b.answer) {
            nodes.push(...getNodes(b.answer));
          } else {
            nodes.push({
              type: 'paragraph',
              source: 'sidebar',
              children: [{text: '[No Answer]', italic: true, color: '#999'}]
            });
          }

          nodes.push({
            type: 'paragraph',
            source: 'sidebar',
            children: [{text: ''}]
          });
        }
      });

      pendingBulkInsertRef.current = true;
      Editor.withoutNormalizing(editor, () => {
        try {
          const safeNodes = normalizeSlateNodesForInsert(nodes);
          insertSidebarNodesAtDocumentEnd(editor, safeNodes, {
            afterReflowMs: 300
          });
        } catch (e) {
          console.error('Error in field-group insert:', e);
        }
      });
      pendingBulkInsertRef.current = false;
    } else if (content.type === 'bulk-qa' || content.type === 'qa') {
      const nodes: any[] = [];
      const items =
        content.type === 'bulk-qa'
          ? content.items || []
          : [{question: content.question, answer: content.answer}];
      items.forEach((item: any) => {
        if (item?.kind === 'heading') {
          const ht = String(item.text ?? '').trim();
          if (!ht) return;
          const level = item.level ?? 'one';
          const headingType = sidebarHeadingLevelToSlateType(level);
          nodes.push({
            type: headingType,
            source: 'sidebar',
            children: [{text: ht}]
          });
          nodes.push({
            type: 'paragraph',
            source: 'sidebar',
            children: [{text: ''}]
          });

          return;
        }

        const qLabel = String(item?.question ?? '').trim();
        if (qLabel) {
          nodes.push({
            type: 'paragraph',
            source: 'sidebar',
            children: [{text: qLabel, bold: true}]
          });
        }

        if (item.answer) nodes.push(...getNodes(item.answer));
        else
          nodes.push({
            type: 'paragraph',
            source: 'sidebar',
            children: [{text: '[No Answer]', italic: true, color: '#999'}]
          });
        nodes.push({
          type: 'paragraph',
          source: 'sidebar',
          children: [{text: ''}]
        });
      });
      const isBulk = content.type === 'bulk-qa';
      pendingBulkInsertRef.current = isBulk;
      Editor.withoutNormalizing(editor, () => {
        try {
          const safeNodes = normalizeSlateNodesForInsert(nodes);
          insertSidebarNodesAtDocumentEnd(editor, safeNodes, {
            afterReflowMs: isBulk ? 300 : undefined
          });
        } catch (e) {
          console.error('Error in bulk insert:', e);
        }
      });
      pendingBulkInsertRef.current = false;
    } else {
      const nodes = getNodes(content);
      if (
        typeof content === 'string' &&
        nodes.length === 1 &&
        nodes[0].children.length === 1 &&
        !nodes[0].children[0].bold
      ) {
        Transforms.insertText(editor, content);
      } else {
        Editor.withoutNormalizing(editor, () => {
          try {
            const safeNodes = normalizeSlateNodesForInsert(nodes);
            if (!safeNodes.length) return;
            let insertPoint: any = null;
            try {
              insertPoint = editor.selection
                ? editor.selection
                : Editor.end(editor, []);
            } catch (e) {}

            Transforms.deselect(editor);
            if (insertPoint) {
              Transforms.insertNodes(editor, safeNodes, {
                select: false,
                at: insertPoint
              });
            } else {
              Transforms.insertNodes(editor, safeNodes, {select: false});
            }
          } catch (e) {}
        });
      }
    }
  });
  focusAndScrollToSelection(editor);
}

/**
 * Clears all sidebar-inserted content from the editor.
 * Previously triggered via window event.
 */
export function clearSidebarContent(editor: Editor): void {
  deselectIfInvalid(editor);
  Editor.withoutNormalizing(editor, () => {
    editor.selection = null;
    try {
      Transforms.deselect(editor);
    } catch {
      // ignore
    }

    Transforms.removeNodes(editor, {
      at: [],
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.source === 'sidebar'
    });
    (editor as any).history = {undos: [], redos: []};
    editor.onChange();
  });
}

/** @deprecated Use clearSidebarContent — confirmation is handled by the caller */
export function processClearSidebarContent(editor: Editor): void {
  clearSidebarContent(editor);
}
